import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { NavLink, Outlet, useNavigate, useSearchParams } from 'react-router-dom';
import type { LocationType } from '@cuito/types';
import { locationsApi } from '../api/locations';
import type { LocationView } from '../api/stock';
import { isShelvable } from '../lib/locationList';
import { useAuthStore } from '../stores/authStore';
import { fetchOrderQueue } from './api/orders';
import { fetchSnapshot } from './api/sync';
import OfflineStatusBar from './components/OfflineStatusBar';
import SwUpdateToast from './components/SwUpdateToast';
import { useOfflineQueue } from './offline/useOfflineQueue';
import {
  readCashierLocationId,
  readDeviceId,
  readSoundEnabled,
  writeCashierLocationCode,
  writeCashierLocationId,
  writeSoundEnabled,
} from './lib/prefs';
import { useSwUpdate } from './pwa/useSwUpdate';
import { connectCashierSocket } from './realtime/socket';
import { useOrderStream, type OrderStream, type OrderStreamDeps } from './realtime/useOrderStream';

/**
 * 收银端外壳（§3.3、§3.4）。
 *
 * 挂在 `AppLayout` **之外**：收银端是触摸优先的独立外壳（没有侧边栏、没有管理端导航），
 * 顶栏三个徽标与底部 tab 就是它的全部导航。连接状态由**这一处**持有（`useOrderStream`），
 * 页面通过 `CashierStreamContext` 读同一份流 —— 每个页面各连一次会让
 * `cashier:<deviceId>` 房间收到 N 份重复事件，也让「徽标说实时、页面说断开」成为可能。
 *
 * **`?locationId=<uuid>` 是 Chain E 验收脚本的定位接口**：命中即绑定，未命中必须
 * **回落且说出来**，绝不静默换地点、绝不白屏（静默换地点会让验收在错误的地点上报绿）。
 *
 * **`locationId` 也在同一个上下文里交出去**（`OrderStream.locationId`）：它是布局级状态，
 * 页面必须**订阅**它，不许用 `useState(() => readCashierLocationId())` 在挂载时快照 ——
 * 子页面先挂载、`GET /locations` 后回来（冷启动深链 `/cashier/scan?locationId=…`）时，
 * 快照只读到 `null` 且再也不重读，扫码页取单与手动建单的取数都会彻底失效
 * （Task 24 验收缺陷 3，P0；Task 18 偏离 ⑥ 是同源的第二处）。
 */
export const CashierStreamContext = createContext<OrderStream | null>(null);

export function useCashierStream(): OrderStream {
  const stream = useContext(CashierStreamContext);
  if (stream === null) {
    // 拿不到流就静默降级的话，页面会「看起来正常但永远不更新」；直接抛出把接线错误暴露在测试里
    throw new Error('useCashierStream 必须在 CashierLayout 之内使用');
  }
  return stream;
}

const CONNECTION_TEXT = {
  live: '实时',
  polling: '轮询模式',
  manual: '纯扫码',
} as const;

const MODE_TEXT: Partial<Record<LocationType, string>> = { SHOP: '门店', EVENT: '展会' };

const TABS = [
  { to: '/cashier', label: '接单', testId: 'cashier-tab-board', end: true },
  { to: '/cashier/scan', label: '扫码', testId: 'cashier-tab-scan', end: false },
  { to: '/cashier/new', label: '建单', testId: 'cashier-tab-new', end: false },
  /**
   * Phase 9 追加的账本 tab（§3.3：「收银端 tab `账本`、工作台 `账本` 入口」）。
   *
   * 只加 tab、不加权限键：`/cashier/ledger` 复用社团后台那一份账本页，
   * 权限面由它自己（`ledger:read` 的服务端守卫 + `ledger:refund` 决定退款入口）兜住。
   */
  { to: '/cashier/ledger', label: '账本', testId: 'cashier-tab-ledger', end: false },
  { to: '/cashier/settings', label: '设置', testId: 'cashier-tab-settings', end: false },
] as const;

export default function CashierLayout() {
  const navigate = useNavigate();
  const logout = useAuthStore((s) => s.logout);
  const [searchParams] = useSearchParams();
  const requestedLocationId = searchParams.get('locationId');

  const [locationId, setLocationId] = useState<string | null>(() => readCashierLocationId());
  const [locations, setLocations] = useState<LocationView[]>([]);
  const [locationNotice, setLocationNotice] = useState<string | null>(null);
  const [soundEnabled, setSoundEnabled] = useState<boolean>(() => readSoundEnabled());

  useEffect(() => {
    void locationsApi
      .list()
      .then((all) => {
        const usable = all.filter((location) => isShelvable(location.type));
        setLocations(usable);
        const isUsable = (id: string | null): id is string =>
          id !== null && usable.some((location) => location.id === id);
        const remembered = readCashierLocationId();

        // ① 深链参数优先：验收脚本靠它把多个浏览器上下文钉在不同地点
        if (requestedLocationId !== null) {
          if (isUsable(requestedLocationId)) {
            setLocationId(requestedLocationId);
            writeCashierLocationId(requestedLocationId);
            setLocationNotice(null);
            return;
          }
          // 给了但不可用（不存在 / 无权 / 不是门店或展会）：回落，并且**说出来**
          if (isUsable(remembered)) {
            setLocationId(remembered);
            setLocationNotice('该地点不可用，已回到上次选择的地点');
            return;
          }
          setLocationId(null);
          setLocationNotice('该地点不可用，请选择地点');
          return;
        }

        // ② 没有深链参数：上次选择的地点 > 第一个可用地点（行为与本 Phase 之前一致）
        if (isUsable(remembered)) {
          setLocationId(remembered);
          return;
        }
        setLocationId(usable[0]?.id ?? null);
      })
      .catch(() => setLocations([]));
  }, [requestedLocationId]);

  const deps = useMemo<OrderStreamDeps>(
    () => ({
      createSocket: (handlers) =>
        connectCashierSocket(
          { token: useAuthStore.getState().accessToken ?? '', deviceId: readDeviceId() },
          handlers,
        ),
      fetchQueue: fetchOrderQueue,
    }),
    [],
  );

  const stream = useOrderStream(locationId, deps);
  const swUpdate = useSwUpdate();
  const current = locations.find((location) => location.id === locationId) ?? null;
  /**
   * 待上传计数（§6.8 的 `离线 · 已缓存 N 个地点 · N 笔待上传`）。
   *
   * Phase 9 起它是**真值**：`useOfflineQueue` 订阅队列的修订号（`offlineQueueRevision`），
   * 入队 / 同步 / 清条都会让这一行重新渲染 —— 不是定时轮询（§6.8 实现要点 2）。
   * 本机没有 IndexedDB（jsdom、老浏览器）时队列为 `null`，计数如实为 0。
   */
  const offline = useOfflineQueue();

  /**
   * 把当前地点的**地点码**写进本地偏好（Phase 9）。
   *
   * 订单码里带着签发时的 `locationCode`，而离线校验（契约 §20.7 第 8 步）需要一个
   * 本机就能读到的对照物：`GET /locations` 断网时只能靠 SW 缓存，缓存里没有就无从判定。
   * 写入时机是**地点解析出来的那一刻**（三条路径都在这一条 effect 里：深链参数、顶栏切换、
   * 冷启动回落），未选地点时删掉旧码 —— 否则会拿上一个店的码去比，比「不校验」更坏。
   */
  useEffect(() => {
    writeCashierLocationCode(current?.code ?? null);
  }, [current]);

  /**
   * **进入地点 ⇒ 无条件拉一次快照**（契约 §18.1 的强制拉取、`REQ-NFR-012`、R-06 ③）。
   *
   * 「进入地点」的三条路径都在这一条依赖里收口：深链 `?locationId=<uuid>`（挂载时 `locationId`
   * 就是这个值）、顶栏切换地点选择器、PWA 冷启动落到 `start_url=/cashier` 之后由 `GET /locations`
   * 解析出地点（`locationId` 从 `null` 变成有值）。三条都会让本 effect 跑一次。
   *
   * ⚠️ 判据是**请求本身**，不是「缓存里有没有」：`cuito-snapshot`（`StaleWhileRevalidate`）与
   * IndexedDB 只服务于断网兜底，在线时的权威源永远是这一次请求。跳过它的症状是
   * 「店员进了地点、面板显示的是三天前的价格」—— 界面不报错、测试全绿，而价格是错的。
   *
   * 失败**不阻断面板**：实时/轮询那两条通道照常工作，快照只是离线兜底的那一份。
   * 这里也**不**把响应写进任何状态 —— Task 8 不做 IndexedDB 持久化（离线队列与快照落盘是 Task 9）。
   */
  useEffect(() => {
    if (locationId === null) return;
    void fetchSnapshot(locationId).catch(() => {
      // 快照拉取失败不是面板级错误：徽标与队列由实时/轮询通道说了算
    });
  }, [locationId]);

  return (
    <CashierStreamContext.Provider value={stream}>
      <div className="flex h-screen w-full flex-col bg-white">
        <header className="flex items-center justify-between gap-3 border-b-2 border-black px-4 py-2">
          <div className="flex items-center gap-2">
            {/*
             * 模式徽标（§3.3 验收：「切换到 event_b 后期望 mode-badge 文案为「展会」」）。
             * 收银端只接门店与展会两种可上架地点（`isShelvable`），仓库/寄售暂存不出现。
             */}
            <span
              data-testid="mode-badge"
              className="rounded border border-black px-1.5 py-0.5 text-xs font-bold"
            >
              {current === null ? '未选择地点' : (MODE_TEXT[current.type] ?? '地点')}
            </span>
            <span className="text-sm font-bold text-gray-500">{current?.name ?? '—'}</span>

            {/*
             * 地点切换 = 门店/展会**模式切换**（§1.6、路线图 §1.6 的验收：切到展会地点后
             * `mode-badge` 文案为「展会」，切回门店为「门店」）。放在顶栏而不是 `/cashier/settings`：
             * `locationId` 是**布局级状态**（房间、模式徽标、每个页面的取数都依赖它），
             * 放到子页面就得把状态提升或另开 context，而顶栏本来就是「当前地点」的显示位（附录 A25/A27）。
             *
             * 切换本身**只做两件事**：写回本地偏好 + 让下面那个连接 effect 用新 `locationId` 重建
             * （`useOrderStream` 另有一半：换地点先清空本地队列）。它**不发任何 HTTP 请求**，
             * 也**不碰**子页面的状态（购物车因此原样留在 `/cashier/new` 上）。
             */}
            <select
              data-testid="location-select"
              aria-label="收银地点"
              value={locationId ?? ''}
              onChange={(event) => {
                const next = event.target.value === '' ? null : event.target.value;
                setLocationId(next);
                writeCashierLocationId(next);
                setLocationNotice(null);
              }}
              className="min-h-[44px] rounded-lg border-2 border-black bg-white px-2 text-sm font-bold"
            >
              <option value="">未选择地点</option>
              {locations.map((location) => (
                <option key={location.id} value={location.id}>
                  {MODE_TEXT[location.type] ?? '地点'} · {location.name}
                </option>
              ))}
            </select>
          </div>

          <div className="flex items-center gap-2">
            {/*
             * 连接徽标（§6.8 降级徽标的形态：`rounded bg-blue-100 px-1.5 py-0.5 text-xs text-blue-700`）。
             * 纯扫码是**三级降级**，文案必须逐字是「纯扫码」（§7.5）。
             */}
            <span
              data-testid="connection-badge"
              className="rounded bg-blue-100 px-1.5 py-0.5 text-xs text-blue-700"
            >
              {locationId === null ? '未选择地点' : CONNECTION_TEXT[stream.level]}
            </span>
            <button
              type="button"
              data-testid="sound-toggle"
              aria-pressed={soundEnabled}
              aria-label={soundEnabled ? '关闭新订单提示音' : '打开新订单提示音'}
              onClick={() => {
                const next = !soundEnabled;
                setSoundEnabled(next);
                writeSoundEnabled(next);
              }}
              className={`flex h-10 w-10 items-center justify-center rounded-lg border-2 border-black text-lg font-bold active:bg-gray-100 ${
                soundEnabled ? '' : 'text-gray-400'
              }`}
            >
              🔔
            </button>
          </div>
        </header>

        {/*
         * 离线状态条（§6.8 的「刚离线」行；testid `offline-status-bar`）。
         *
         * **与顶栏的 `connection-badge` 是两个元素**（§5.6 的 Phase 9 冻结条款要求分别断言）：
         * 这里是整宽黑条 `离线 · 已缓存 N 个地点 · N 笔待上传`，徽标是那个小蓝标
         * `实时` / `轮询模式` / `纯扫码`。把两者合成一个元素，「只实现了一个」就会全绿。
         *
         * `cachedLocations` 取 `locations.length`：那份列表来自 `GET /locations`（`cuito-api` 规则
         * 下是 NetworkFirst），断网时手里能拿出来渲染的就是它 —— 「已缓存 N 个地点」说的正是这个数。
         * `pendingCount` 自 Phase 9 起是**队列的真实长度**（`useOfflineQueue` 订阅修订号，
         * 不是定时轮询）：入队一件，这一行立刻变；同步成功一件，立刻减。
         */}
        <OfflineStatusBar
          level={stream.level}
          cachedLocations={locations.length}
          pendingCount={offline.items.length}
        />

        {locationNotice !== null && (
          <p
            data-testid="location-notice"
            role="status"
            className="border-b-2 border-black bg-gray-50 px-4 py-1 text-sm text-red-600"
          >
            {locationNotice}
          </p>
        )}

        <main className="flex-1 overflow-y-auto p-4">
          <Outlet />
        </main>

        <nav className="flex border-t-2 border-black">
          {TABS.map((tab) => (
            <NavLink
              key={tab.testId}
              to={tab.to}
              end={tab.end}
              data-testid={tab.testId}
              className={({ isActive }) =>
                `flex min-h-[56px] flex-1 items-center justify-center text-sm font-bold ${
                  isActive ? 'bg-black text-white' : 'bg-white text-black'
                }`
              }
            >
              {tab.label}
            </NavLink>
          ))}
          <button
            type="button"
            data-testid="cashier-logout"
            onClick={() => {
              logout();
              navigate('/login', { replace: true });
            }}
            className="flex min-h-[56px] flex-1 items-center justify-center border-l-2 border-black text-sm font-bold text-gray-600 active:bg-gray-100"
          >
            退出登录
          </button>
        </nav>

        {/*
         * 更新提示（R-06 ②、§5.6）：底部常驻黑条，`有新版本，点击刷新` + 「刷新」。
         *
         * 挂在外壳而不是 `main` 里：它是**全局**的（换 tab、换地点都不该把它冲掉），
         * 而 §5.6 的触发时机第一条就是「PWA 启动时比对一次」—— `start_url` 就是 `/cashier`。
         * 三条触发路径（启动比对 / SW 进入 waiting / 手动刷新后的下一次比对）见 `useSwUpdate`。
         */}
        <SwUpdateToast open={swUpdate.updateAvailable} onRefresh={swUpdate.refresh} />
      </div>
    </CashierStreamContext.Provider>
  );
}
